/**
 * Helpers for WhatsApp deep links: admin can store a full URL (wa.me, api.whatsapp.com, etc.)
 * or legacy digits-only phone (normalized to api.whatsapp.com).
 */

/** Add https:// when user pastes wa.me/... or api.whatsapp.com/... without a scheme */
export function normalizeWhatsAppLinkInput(input: string): string {
  const t = (input || '').trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t)) return t
  const lower = t.toLowerCase()
  if (lower.startsWith('wa.me/') || lower.startsWith('www.wa.me/')) {
    return `https://${t.replace(/^\/+/, '')}`
  }
  if (
    lower.startsWith('api.whatsapp.com') ||
    lower.startsWith('www.api.whatsapp.com') ||
    lower.startsWith('chat.whatsapp.com/') ||
    lower.startsWith('www.chat.whatsapp.com/')
  ) {
    return `https://${t.replace(/^\/+/, '')}`
  }
  return t
}

/** True if this should be treated as a stored link (https URL), not legacy digits */
export function isWhatsAppHttpUrl(value: string): boolean {
  const n = normalizeWhatsAppLinkInput((value || '').trim())
  return /^https?:\/\//i.test(n)
}

/** Legacy: digits only; 10-digit assumed India → 91 prefix */
export function normalizePhoneDigits(value: string): string {
  const digits = (value || '').replace(/\D/g, '')
  if (digits.length === 10) return '91' + digits
  return digits
}

export function buildWhatsAppUrlFromDigits(digitsOrE164: string): string {
  const phone = normalizePhoneDigits(digitsOrE164)
  if (!phone) return 'https://api.whatsapp.com/send?phone='
  return `https://api.whatsapp.com/send?phone=${phone}`
}

/**
 * Merge optional pre-filled message into an existing WhatsApp URL (sets/overrides `text` query param).
 */
export function appendPrefillToWhatsAppUrl(baseUrl: string, prefillText?: string): string {
  const base = (baseUrl || '').trim()
  if (!prefillText) return base
  try {
    const url = new URL(base)
    url.searchParams.set('text', prefillText)
    return url.toString()
  } catch {
    const sep = base.includes('?') ? '&' : '?'
    return `${base}${sep}text=${encodeURIComponent(prefillText)}`
  }
}

/**
 * Resolve stored setting: full URL as-is (after normalizing scheme), or legacy digits → default send URL.
 */
export function resolveWhatsAppHref(stored: string, prefillText?: string): string {
  const raw = (stored || '').trim()
  if (!raw) return 'https://api.whatsapp.com/send?phone='
  const normalized = normalizeWhatsAppLinkInput(raw)
  if (/^https?:\/\//i.test(normalized)) {
    return appendPrefillToWhatsAppUrl(normalized, prefillText)
  }
  return appendPrefillToWhatsAppUrl(buildWhatsAppUrlFromDigits(raw), prefillText)
}
